import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';

// Hero Section
const AboutHero = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="about-hero">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-white mb-4 uppercase">
        About Sunlight Grounds Care
      </h1>
      <p className="text-lg text-slate-300 max-w-2xl">
        Structured exterior maintenance programs for high-value residential and commercial properties throughout Southern California.
      </p>
    </div>
  </section>
);

// Mission Section
const MissionSection = () => (
  <section className="bg-slate-50 py-16" data-testid="mission-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold text-[#1a2e44] mb-6">
          Our Approach
        </h2>
        <p className="text-slate-600 mb-6">
          We manage exterior properties through structured maintenance programs built around planning, documentation, and accountability.
        </p>
        <p className="text-slate-600 mb-6">
          Rather than reacting to service requests, we focus on proactive property oversight designed to maintain appearance, safety, and long-term asset value.
        </p>
        <p className="text-slate-600">
          Each property receives a customized maintenance program developed through detailed assessment and aligned with the client's priorities.
        </p>
      </div>
    </div>
  </section>
);

// Operating Principles Section
const PrinciplesSection = () => (
  <section className="bg-white py-16" data-testid="principles-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold text-[#1a2e44] mb-6">
          Our Operating Principles
        </h2>
        <p className="text-slate-600 mb-8">
          Our service delivery is built around five operating principles that guide every property we manage:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {['Clarity', 'Communication', 'Consistency', 'Documentation', 'Accountability'].map((principle, index) => (
            <div key={index} className="text-center py-8 px-4 bg-slate-50 border border-slate-200">
              <span className="text-base font-bold text-[#1a2e44]">{principle}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Standards Section
const StandardsSection = () => {
  const standards = [
    {
      title: 'Licensed & Insured',
      description: 'Fully licensed and insured with commercial liability coverage for all services performed.'
    },
    {
      title: 'Documented Protocols',
      description: 'Documented service procedures that ensure consistent maintenance across every property.'
    },
    {
      title: 'Trained Personnel',
      description: 'Our teams receive ongoing training in safety, equipment operation, and property care standards.'
    },
    {
      title: 'Clear Communication',
      description: 'Clear communication through regular updates, service documentation, and responsive support.'
    }
  ];

  return (
    <section className="bg-slate-50 py-16" data-testid="standards-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-[#1a2e44] mb-8">
          Standards & Accountability
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {standards.map((standard, index) => (
            <div key={index} className="border-l-4 border-[#1a2e44] pl-6">
              <h3 className="text-lg font-bold text-[#1a2e44] mb-2">
                {standard.title}
              </h3>
              <p className="text-slate-600 text-sm">
                {standard.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Area Section
const ServiceAreaSection = () => (
  <section className="bg-white py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-2xl font-semibold text-[#1a2e44] mb-6">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl">
        We provide exterior maintenance services throughout Los Angeles County and surrounding areas of Southern California. Contact us to confirm service availability for your location.
      </p>
    </div>
  </section>
);

// CTA Section
const AboutCTA = () => (
  <section className="bg-[#1a2e44] py-16" data-testid="about-cta">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-white mb-4">
        Schedule a Property Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        Schedule a site assessment to discuss your property's requirements.
      </p>
      <Link
        to="/contact?intent=assessment"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="about-cta-btn"
      >
        Request a Site Assessment
      </Link>
    </div>
  </section>
);

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About Us | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="about-page">
      <AboutHero />
      <MissionSection />
      <PrinciplesSection />
      <StandardsSection />
      <ServiceAreaSection />
      <AboutCTA />
    </div>
  );
}
